import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select } from '@any-ui-react/core';
import { Lens } from '@hookform/lenses';

import { useVariableUrlInput } from '../../hooks';
import { useLineMessageStore } from '../../store';
import { PostbackAction } from '../../types';
import { UrlActionManualInput, UrlActionVariableSelect } from '../../url-action-block';

const ButtonTypePostbackActionSelect = ({ lens }: { lens: Lens<PostbackAction> }) => {
  const { t } = useTranslation('crm');
  const { field } = useController(lens.interop());
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);
  const { enableVariableUrl } = useVariableUrlInput();

  return (
    <Select
      {...field}
      label={t('crm.lineMessageForm.fields.actionType.label')}
      className="w-full"
      required
      readOnly={isFormDisabled}
      data={[
        {
          label: t('crm.lineMessageForm.fields.urlAction.select.options.manual'),
          value: PostbackAction.enum.URL_MANUAL,
        },
        {
          label: t('crm.lineMessageForm.fields.actionType.postback'),
          value: PostbackAction.enum.POSTBACK,
        },
        ...(enableVariableUrl
          ? [
              {
                label: t('crm.lineMessageForm.fields.urlAction.select.options.variable'),
                value: PostbackAction.enum.URL_VARIABLE,
              },
            ]
          : []),
      ]}
    />
  );
};

export const ButtonTypeActionTypeController = ({
  lens,
  postbackAction,
  trackingEnabled,
}: {
  lens: Lens<{ postbackAction: PostbackAction; manual: string; variable: string }>;
  postbackAction: PostbackAction;
  trackingEnabled?: boolean;
}) => {
  const { t } = useTranslation('crm');

  return (
    <div className="flex flex-col gap-y-2">
      <ButtonTypePostbackActionSelect lens={lens.focus('postbackAction')} />
      {postbackAction === PostbackAction.enum.URL_MANUAL && (
        <UrlActionManualInput
          required
          lens={lens.focus('manual')}
          trackingEnabled={trackingEnabled}
        />
      )}
      {postbackAction === PostbackAction.enum.URL_VARIABLE && (
        <UrlActionVariableSelect required lens={lens.focus('variable')} />
      )}
      {postbackAction === PostbackAction.enum.POSTBACK && (
        <p className="text-gray-7 mb-1.5">
          {t('crm.lineMessageForm.fields.actionType.description')}
        </p>
      )}
    </div>
  );
};
